import {
  Box,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Icon,
  chakra,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { KYDEventBundle } from "@common/interfaces/KYDEvent";
import { Link } from "react-router-dom";
import { useState, useMemo } from "react";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";

export const BundlesTable = ({
  bundles,
  ...props
}: {
  bundles: KYDEventBundle[];
}) => {
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const sortedBundles = useMemo(() => {
    return [...bundles].sort((a, b) => {
      if (sortBy === "title") {
        return sortOrder === "asc" 
          ? a.title.localeCompare(b.title)
          : b.title.localeCompare(a.title);
      } else if (sortBy === "events") {
        return sortOrder === "asc"
          ? a.events.length - b.events.length
          : b.events.length - a.events.length;
      } else if (sortBy === "created_at") {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return sortOrder === "asc"
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      }
      return 0;
    });
  }, [bundles, sortBy, sortOrder]);

  return (
    <Table {...props}>
      <Thead>
        <Tr>
          <Th cursor="pointer" onClick={() => handleSort("title")}>
            <HStack spacing="1">
              <Text>Name</Text>
              <Box w="3" h="3" display="inline-flex" alignItems="center">
                {sortBy === "title" && (
                  <Icon
                    as={sortOrder === "asc" ? TriangleUpIcon : TriangleDownIcon}
                    aria-label={`sorted ${sortOrder}`}
                    boxSize="3"
                  />
                )}
              </Box>
            </HStack>
          </Th>
          <Th cursor="pointer" onClick={() => handleSort("events")}>
            <HStack spacing="1">
              <Text>Events</Text>
              <Box w="3" h="3" display="inline-flex" alignItems="center">
                {sortBy === "events" && (
                  <Icon
                    as={sortOrder === "asc" ? TriangleUpIcon : TriangleDownIcon}
                    aria-label={`sorted ${sortOrder}`}
                    boxSize="3"
                  />
                )}
              </Box>
            </HStack>
          </Th>
          <Th cursor="pointer" onClick={() => handleSort("created_at")}>
            <HStack spacing="1">
              <Text>Created At</Text>
              <Box w="3" h="3" display="inline-flex" alignItems="center">
                {sortBy === "created_at" && (
                  <Icon
                    as={sortOrder === "asc" ? TriangleUpIcon : TriangleDownIcon}
                    aria-label={`sorted ${sortOrder}`}
                    boxSize="3"
                  />
                )}
              </Box>
            </HStack>
          </Th>
          <Th></Th>
        </Tr>
      </Thead>
      <Tbody>
        {sortedBundles.map((bundle: KYDEventBundle, index: number) => (
          <Tr key={bundle.id}>
            <Link to={`/bundles/${bundle.id}`}>
              <Td>
                <HStack spacing="3">
                  <Box>
                    <Text fontWeight="medium">{bundle.title}</Text>
                  </Box>
                </HStack>
              </Td>
            </Link>
            <Td>
              <Text color="muted">{bundle.events.length}</Text>
            </Td>
            <Td>
              <Text color="muted">{bundle.display_created_at}</Text>
            </Td>
            <Td>
              <ExternalLinkIcon
                cursor={"pointer"}
                onClick={() => window.open(bundle.link)}
              />
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
};
